"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosBlendSuggestionConfigUpdateInputSchema = exports.APosBlendSuggestionConfigSchema = exports.APosBlendBalanceRulesSchema = exports.APosBlendPricingDefaultsSchema = exports.APosRoundingStrategySchema = exports.APosBlendTweakPricingModeSchema = exports.APosBlendPricingModeSchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
/**
 * Module 29 — Oil Blending (Phase 2)
 *
 * BlendSuggestionConfig — per-tenant configuration for the rule-based blend
 * suggestion engine + pricing defaults that drive custom / signature / tweak
 * blend pricing.
 *
 * Table layout (provisioned by infrastructure/dynamodb-agenticpos):
 *   PK = tenantId
 *   SK = configKey   (singleton row — always `_`)
 *
 * Multi-tenant: a single row per tenantId. There is exactly one active config
 * per tenant; we use `configKey='_'` as the SK so the table schema supports
 * future per-locale / per-channel overrides without a migration.
 *
 * Backward-compatibility: the existing `refill_placeholder_variant_ids` map
 * was written into this same row by Slice 10 (P1) — we preserve it here so
 * P2 admin endpoints don't accidentally clobber the P1 fields.
 */
exports.APosBlendPricingModeSchema = zod_1.z.enum([
    'flat_per_bottle',
    'ingredient_cost_plus',
    'fixed_per_recipe',
]);
exports.APosBlendTweakPricingModeSchema = zod_1.z.enum([
    'inherit_from_parent',
    'flat_per_bottle',
    'ingredient_cost_plus',
    'fixed_per_recipe',
]);
exports.APosRoundingStrategySchema = zod_1.z.enum(['nearest_99', 'nearest_5', 'nearest_dollar']);
// ---------------------------------------------------------------------------
// pricing_defaults sub-shape
// ---------------------------------------------------------------------------
exports.APosBlendPricingDefaultsSchema = zod_1.z.object({
    // Mode-per-blend-type. `custom` and `signature` are the two stored
    // BlendRecipe.blend_type values. `tweak` is a sub-mode for child recipes
    // (parent_recipe_id != null).
    default_mode_for_custom_blends: exports.APosBlendPricingModeSchema.default('flat_per_bottle'),
    default_mode_for_signatures: exports.APosBlendPricingModeSchema.default('fixed_per_recipe'),
    default_mode_for_tweaks: exports.APosBlendTweakPricingModeSchema.default('inherit_from_parent'),
    // Convenience aliases retained for legacy callers (Phase 1 task spec uses
    // these flat names). They mirror the canonical fields above.
    custom: exports.APosBlendPricingModeSchema.optional(),
    signature: exports.APosBlendPricingModeSchema.optional(),
    tweak: exports.APosBlendTweakPricingModeSchema.optional(),
    // Bottle-size → flat-price-cents lookup. Keys are bottle_size_ml as a string
    // because DynamoDB JSON serialization stringifies numeric map keys.
    flat_prices_by_bottle_ml: zod_1.z.record(zod_1.z.string(), zod_1.z.number().int().nonnegative()).default({}),
    flat_prices_by_bottle_size: zod_1.z
        .record(zod_1.z.string(), zod_1.z.number().int().nonnegative())
        .optional(),
    // ingredient_cost_plus mode controls
    default_markup_multiplier: zod_1.z.number().nonnegative().default(4.0),
    default_labor_charge_cents: zod_1.z.number().int().nonnegative().default(200),
    rounding_strategy: exports.APosRoundingStrategySchema.default('nearest_99'),
    // Per-tier multipliers — overrides applied on top of `default_markup_multiplier`.
    // Used by suggestion engine when a fragrance has `markup_tier` set.
    markup_tier_multipliers: zod_1.z
        .object({
        standard: zod_1.z.number().nonnegative().default(1.0),
        premium: zod_1.z.number().nonnegative().default(1.5),
        luxury: zod_1.z.number().nonnegative().default(2.5),
    })
        .default({ standard: 1.0, premium: 1.5, luxury: 2.5 }),
    // Auto-escalation safeguard. When a custom blend uses `flat_per_bottle` but
    // the ingredient cost crosses this threshold, automatically switch to
    // `ingredient_cost_plus` to protect margin.
    auto_escalate_to_cost_plus: zod_1.z.boolean().default(true),
    // 4-digit cents precision (e.g. 500 = $5.00). Field stored as integer cents.
    auto_escalate_threshold_cents: zod_1.z.number().int().nonnegative().max(9999).default(500),
    // Refill / BYOB defaults
    refill_enabled: zod_1.z.boolean().default(true),
    refill_pricing_mode: zod_1.z
        .enum(['recipe_only', 'recipe_minus_discount', 'fixed_refill_price'])
        .default('recipe_minus_discount'),
    refill_discount_cents: zod_1.z.number().int().nonnegative().default(200),
    refill_fixed_price_cents_by_bottle_ml: zod_1.z
        .record(zod_1.z.string(), zod_1.z.number().int().nonnegative())
        .default({}),
    refill_loyalty_bonus_points: zod_1.z.number().int().nonnegative().default(50),
});
// ---------------------------------------------------------------------------
// balance_rules sub-shape
// ---------------------------------------------------------------------------
exports.APosBlendBalanceRulesSchema = zod_1.z.object({
    require_at_least_one_base: zod_1.z.boolean().default(true),
    max_top_notes: zod_1.z.number().int().min(1).max(10).default(2),
    default_ingredient_count: zod_1.z.number().int().min(2).max(6).default(3),
    // Target % distribution across top / middle / base notes — used by the
    // suggestion engine to score "balance_ok".
    target_top_percent: zod_1.z.number().min(0).max(100).default(20),
    target_middle_percent: zod_1.z.number().min(0).max(100).default(50),
    target_base_percent: zod_1.z.number().min(0).max(100).default(30),
});
// ---------------------------------------------------------------------------
// Main schema
// ---------------------------------------------------------------------------
exports.APosBlendSuggestionConfigSchema = zod_1.z
    .object({
    tenantId: id_schema_1.TenantIdSchema,
    configKey: zod_1.z.string().default('_').describe('SK — singleton, always "_"'),
    balance_rules: exports.APosBlendBalanceRulesSchema.default({}),
    // Per-ingredient-count ratio templates. Keys are stringified count.
    default_ratios_by_complexity: zod_1.z
        .record(zod_1.z.string(), zod_1.z.array(zod_1.z.number().min(0).max(100)))
        .default({
        '2': [70, 30],
        '3': [50, 30, 20],
        '4': [40, 30, 20, 10],
        '5': [35, 25, 20, 10, 10],
    }),
    // mood_to_fragrance_weights[purposeTag][scentFamily] = 1..5
    mood_to_fragrance_weights: zod_1.z
        .record(zod_1.z.string(), zod_1.z.record(zod_1.z.string(), zod_1.z.number().min(0).max(5)))
        .default({}),
    // Convenience aliases — equivalent to above, but flatter shape used by
    // some legacy callers / the v2 admin page.
    mood_weights: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).optional(),
    purpose_weights: zod_1.z.record(zod_1.z.string(), zod_1.z.number()).optional(),
    // Phase 3 placeholder. Suggestion engine will boost fragrances matching
    // customer.scentPreferences.likes when this is true.
    respect_customer_preferences: zod_1.z.boolean().default(false),
    pricing_defaults: exports.APosBlendPricingDefaultsSchema.default({}),
    // Flat balance-target convenience fields (mirror balance_rules.target_*).
    balance_target_top_percent: zod_1.z.number().min(0).max(100).optional(),
    balance_target_middle_percent: zod_1.z.number().min(0).max(100).optional(),
    balance_target_base_percent: zod_1.z.number().min(0).max(100).optional(),
    // Direct top-level flat-prices map (legacy / Phase-1 P1.7 callers used this
    // location; canonical location is pricing_defaults.flat_prices_by_bottle_ml).
    flat_prices_by_bottle_size: zod_1.z
        .record(zod_1.z.string(), zod_1.z.number().int().nonnegative())
        .optional(),
    // P1 carry-over: refill placeholder Variant IDs created by
    // seedRefillPlaceholders migration. NEVER write or strip this field in
    // P2 admin endpoints — the P1 sales pipeline depends on it.
    refill_placeholder_variant_ids: zod_1.z
        .object({
        body_oil: zod_1.z.string().optional(),
        essential_oil: zod_1.z.string().optional(),
    })
        .partial()
        .optional(),
    is_active: zod_1.z.boolean().default(true),
})
    .merge(timestamps_schema_1.TimestampsSchema);
/**
 * Input shape for PUT /config/suggestion. All fields optional — caller may
 * supply a partial config and we deep-merge against the existing row.
 */
exports.APosBlendSuggestionConfigUpdateInputSchema = exports.APosBlendSuggestionConfigSchema
    .partial()
    .omit({ tenantId: true, configKey: true, createdAt: true, updatedAt: true });
//# sourceMappingURL=blend-suggestion-config.schema.js.map